import { forwardRef, ReactNode, useRef } from "react";

import { Box, Stack, StackProps, Theme, SxProps, useTheme, keyframes } from "@mui/material";

import { mergeSx } from "@/common/helpers/mergeSx";
import { FormIcon } from "@/forms-design-system/FormIcon/FormIcon";
import { FormTypography } from "@/forms-design-system/FormTypography/FormTypography";
import { InvalidThemeErrorMessage } from "@/forms-design-system/InvalidThemeErrorMessage";

const MINIMUM_PROGRESS = 1;
const MINIMUM_CONTRIBUTION = 1;
const TRACK_HEIGHT = "12px";
const HEAD_OVERLAP = "8px";
const TRACK_CAP_RADIUS = "6px";
const MINIMUM_BAR_WIDTH = "6px";
const WIDTH_TRANSITION = "600ms cubic-bezier(0.4, 0, 0.2, 1)";
const FADE_TRANSITION = "420ms ease-out";

const getBloomLayers = (color: string) => ({
  static: [
    `0 0 38px 10px color-mix(in srgb, ${color} 10%, transparent)`,
    `0 0 21px 6px color-mix(in srgb, ${color} 15%, transparent)`,
    `0 0 10px 2px color-mix(in srgb, ${color} 22%, transparent)`,
  ].join(", "),
  rest: `0 0 10px 2px color-mix(in srgb, ${color} 26%, transparent)`,
  peak: `0 0 20px 6px color-mix(in srgb, ${color} 36%, transparent)`,
});

export type FormThermometerMessage = {
  icon: ReactNode;
  text: ReactNode;
};

export type FormThermometerProps = StackProps & {
  label: ReactNode;
  percentage?: number;
  contributionPercentage?: number;
  message?: FormThermometerMessage;
  hidePercentage?: boolean;
  isProgressTransitioned?: boolean;
  components?: {
    progress?: {
      sx?: SxProps<Theme>;
    };
  };
  backgroundContent?: ReactNode;
};

export const getThermometerWidths = (percentage: number, contributionPercentage: number) => {
  const progress = Math.min(Math.max(percentage, MINIMUM_PROGRESS), 100);

  if (contributionPercentage <= 0) {
    return { barWidth: progress, headWidth: 0 };
  }

  const headWidth = Math.min(
    Math.max(contributionPercentage, MINIMUM_CONTRIBUTION),
    Math.max(100 - progress, MINIMUM_CONTRIBUTION)
  );
  return { barWidth: Math.min(progress, 100 - headWidth), headWidth };
};

export const getBarCssWidth = (barWidth: number, minimumBarWidth: string) => `max(${barWidth}%, ${minimumBarWidth})`;

export const getBarBorderRadius = (capRadius: string) => {
  const endRadius = `min(${capRadius}, calc(100% - ${capRadius}))`;
  return `${capRadius} ${endRadius} ${endRadius} ${capRadius} / ${capRadius}`;
};

export const getHeadCssWidth = ({
  barWidth,
  headWidth,
  minimumBarWidth,
  overlap,
}: {
  barWidth: number;
  headWidth: number;
  minimumBarWidth: string;
  overlap: string;
}) => `min(calc(${headWidth}% + ${overlap}), calc(100% - ${getBarCssWidth(barWidth, minimumBarWidth)} + ${overlap}))`;

export const FormThermometer = forwardRef<HTMLDivElement, FormThermometerProps>(
  (
    {
      label,
      percentage = 0,
      contributionPercentage = 0,
      message,
      components,
      sx,
      backgroundContent,
      hidePercentage,
      isProgressTransitioned = true,
      ...rest
    },
    ref
  ) => {
    const theme = useTheme();
    const lastMessageRef = useRef(message);

    if (message) {
      lastMessageRef.current = message;
    }
    const displayedMessage = message ?? lastMessageRef.current;

    if (!theme.palette.forms || !theme.palette.formsComponents?.thermometer) {
      return <InvalidThemeErrorMessage />;
    }

    const thermometerTokens = theme.palette.formsComponents.thermometer;
    const { barWidth, headWidth } = getThermometerWidths(percentage, contributionPercentage);
    const hasHead = headWidth > 0;
    const headOverlap = `min(${HEAD_OVERLAP}, ${getBarCssWidth(barWidth, MINIMUM_BAR_WIDTH)})`;
    const hasMessage = Boolean(message);
    const roundedPercentage = Math.round(percentage);
    const bloom = getBloomLayers(thermometerTokens.contributionBloomColor);

    const bloomPulse = keyframes`
      0% { box-shadow: ${bloom.static}, ${bloom.rest}; animation-timing-function: ease-in-out; }
      40% { box-shadow: ${bloom.static}, ${bloom.peak}; animation-timing-function: ease-in-out; }
      80%, 100% { box-shadow: ${bloom.static}, ${bloom.rest}; }
    `;

    const styles = mergeSx(
      {
        padding: theme.spacing(1.5, 2, 2),
        backgroundColor: thermometerTokens.backgroundColor,
        borderRadius: theme.radius(1),
      },
      sx
    );

    const trackStyles = mergeSx(
      {
        position: "relative",
        zIndex: 1,
        display: "flex",
        height: TRACK_HEIGHT,
        borderRadius: theme.radius(1),
        backgroundColor: thermometerTokens.progressTrackBackgroundColor,
        isolation: "isolate",
      },
      components?.progress?.sx
    );

    return (
      <Stack ref={ref} sx={styles} {...rest}>
        {backgroundContent}
        <Stack gap={1} sx={{ position: "relative", zIndex: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="baseline" gap={2}>
            <FormTypography color={thermometerTokens.textColor} variant="subtitle2" component="div">
              {label}
            </FormTypography>
            {!hidePercentage && (
              <FormTypography color={thermometerTokens.textColor} variant="h6" sx={{ whiteSpace: "nowrap" }}>
                {roundedPercentage}%
              </FormTypography>
            )}
          </Stack>
          <Box
            role="progressbar"
            aria-valuenow={roundedPercentage}
            aria-valuemin={0}
            aria-valuemax={100}
            sx={trackStyles}
          >
            <Box
              data-testid="thermometer-bar"
              sx={{
                position: "relative",
                zIndex: 2,
                flexShrink: 0,
                width: getBarCssWidth(barWidth, MINIMUM_BAR_WIDTH),
                height: "100%",
                marginRight: `calc(-1 * ${headOverlap})`,
                borderRadius: getBarBorderRadius(TRACK_CAP_RADIUS),
                backgroundColor: thermometerTokens.progressBarBackgroundColor,
                transition: isProgressTransitioned ? `width ${WIDTH_TRANSITION}` : "none",
              }}
            />
            <Box
              data-testid="thermometer-contribution-head"
              sx={{
                position: "relative",
                zIndex: 1,
                flexShrink: 0,
                width: hasHead
                  ? getHeadCssWidth({ barWidth, headWidth, minimumBarWidth: MINIMUM_BAR_WIDTH, overlap: headOverlap })
                  : 0,
                height: "100%",
                borderRadius: TRACK_CAP_RADIUS,
                backgroundColor: thermometerTokens.contributionHeadColor,
                opacity: hasHead ? 1 : 0,
                boxShadow: `${bloom.static}, ${bloom.rest}`,
                animation: `${bloomPulse} 2s infinite`,
                transition: `width ${WIDTH_TRANSITION}, opacity ${FADE_TRANSITION}`,
                "@media (prefers-reduced-motion: reduce)": {
                  animation: "none",
                  transition: "none",
                },
              }}
            />
          </Box>
        </Stack>
        <Box
          aria-live="polite"
          sx={{
            position: "relative",
            zIndex: 1,
            display: "grid",
            gridTemplateRows: hasMessage ? "1fr" : "0fr",
            opacity: hasMessage ? 1 : 0,
            transition: `grid-template-rows 300ms ease-out, opacity ${FADE_TRANSITION}`,
            "@media (prefers-reduced-motion: reduce)": {
              transition: "none",
            },
          }}
        >
          <Box sx={{ minHeight: 0, overflow: "hidden" }}>
            {displayedMessage && (
              <Stack direction="row" alignItems="center" justifyContent="center" gap={0.5} sx={{ paddingTop: 1 }}>
                <FormIcon size="medium">{displayedMessage.icon}</FormIcon>
                <FormTypography variant="body2" vibe="brand" sx={{ textAlign: "center" }}>
                  {displayedMessage.text}
                </FormTypography>
              </Stack>
            )}
          </Box>
        </Box>
      </Stack>
    );
  }
);

FormThermometer.displayName = "FormThermometer";
